import { createSelector } from 'reselect';
import { KioskHardware } from 'store/kioskHardware';
import { GlobalState } from 'store/types';
import { KEY } from './';

const getKiosksState = (state: GlobalState) => state[KEY];

const selectKiosks = createSelector(getKiosksState, (kiosks) => kiosks.kiosks);

const selectKiosk = createSelector(getKiosksState, (kiosks) => kiosks.kiosk);

const selectKioskSchedule = createSelector(getKiosksState, (state) => state.kioskSchedule);

const selectAppVersion = createSelector(getKiosksState, (state) => state.version);

const selectAction = createSelector(getKiosksState, (state) => state.action);

const selectKioskLoginToken = createSelector(getKiosksState, (state) => state.loginToken);

const selectAutoRefresh = createSelector(getKiosksState, (state) => state.autoRefresh);

const selectShowHiddenKiosks = createSelector(getKiosksState, (state) => state.showHiddenKiosks);

const selectMessage = createSelector(getKiosksState, (state) => state.action.message);

const selectExportData = createSelector(getKiosksState, (state) => state.export.data);

const selectExportPending = createSelector(getKiosksState, (state) => state.export.pending);

/**
 * Returns a Map of stock counts by printer display name. Ex:
 * {
 * 'Printer 1' => { '<kiosk[0].id>' => 10, '<kiosk[1].id' => 20 },
 * 'Printer 2' => { '<kiosk[0].id>' => 5, '<kiosk[1].id' => 15 }
 * }
 * The keys are the display names of the printers, and the values are objects
 * that contain the kiosk Ids as keys and the current stock as values.
 */
const selectStockCountsByPrinter = createSelector(selectKiosks, (kiosks) => {
  const stockCountsByPrinter = new Map<string, Record<string, number>>();
  kiosks?.forEach((kiosk) => {
    kiosk?.hardware
      ?.filter((hardware: KioskHardware) => hardware.type === 'printer')
      ?.forEach((printer: KioskHardware) => {
        const displayName = printer.configuration.displayName || '';
        const currentStock = printer.values.currentStock;
        if (!stockCountsByPrinter.has(displayName)) {
          stockCountsByPrinter.set(displayName, {});
        }
        if (currentStock) {
          stockCountsByPrinter.set(displayName, {
            ...stockCountsByPrinter.get(displayName),
            [kiosk.id]: currentStock
          });
        }
      });
  });
  return new Map([...stockCountsByPrinter.entries()].sort());
});

export const selectors = {
  selectKiosks,
  selectMessage,
  selectKiosk,
  selectKioskSchedule,
  selectAppVersion,
  selectAction,
  selectKioskLoginToken,
  selectAutoRefresh,
  selectShowHiddenKiosks,
  selectExportData,
  selectExportPending,
  selectStockCountsByPrinter
};
